// src/components/learning/LearningFlow.tsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { api } from "../../utils/api";
import { FiArrowLeft, FiCheckCircle } from "react-icons/fi";
import TermSelection from "./TermSelection";
import SubjectList from "./SubjectList";
import StrandList from "./StrandList";
import SubStrandList from "./SubStrandList";
import LessonViewer from "./LessonViewer";
import PostLesson from "./PostLesson";
import ModuleRenderer from "./ModuleRenderer";
import { useAuth } from "../../contexts/AuthContext";
import { Lesson } from "../../types/learning";

// Styled Components
const Container = styled.div`
  max-width: 900px;
  margin: 30px auto;
  padding: 30px;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  font-family: "Roboto", sans-serif;
`;

const BackButtonContainer = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  cursor: pointer;
  padding: 8px;
  &:hover {
    color: ${({ theme }) => theme.primaryDark || "#5a17a7"};
  }
`;

const Button = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  background-color: ${({ theme }) => theme.primary || "#7e22ce"};
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: ${({ theme }) => theme.primaryDark || "#5a17a7"};
  }
  &:disabled {
    background-color: #d1d5db;
    cursor: not-allowed;
  }
`;

const ProgressContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
  position: relative;
`;

const ProgressBar = styled.div`
  position: absolute;
  height: 4px;
  background-color: ${({ theme }) => theme.primaryLight || "#c4b5fd"};
  top: 14px;
  left: 0;
  right: 0;
  z-index: 1;
`;

const ProgressFill = styled.div<{ width: string }>`
  position: absolute;
  height: 4px;
  background-color: ${({ theme }) => theme.primary || "#7e22ce"};
  top: 0;
  left: 0;
  width: ${(props) => props.width};
  transition: width 0.3s ease;
  z-index: 2;
`;

const StepCircle = styled.div<{ active: boolean; completed: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${(props) => (props.active ? "bold" : "normal")};
  background-color: ${(props) =>
    props.completed
      ? props.theme.primary || "#7e22ce"
      : props.active
      ? props.theme.primaryLight || "#c4b5fd"
      : "#e5e7eb"};
  color: ${(props) => (props.completed || props.active ? "#fff" : "#6b7280")};
  z-index: 3;
  transition: all 0.3s ease;
`;

const StepLabel = styled.div<{ active: boolean; completed: boolean }>`
  font-size: 0.8rem;
  text-align: center;
  margin-top: 8px;
  color: ${(props) =>
    props.completed
      ? props.theme.primary || "#7e22ce"
      : props.active
      ? "#000"
      : "#6b7280"};
  font-weight: ${(props) => (props.active || props.completed ? "bold" : "normal")};
`;

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 3;
`;

const ModuleProgress = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.primaryLight || "#c4b5fd"}20;
  padding: 12px 16px;
  border-radius: 8px;
  margin: 20px 0;
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid ${({ theme }) => theme.primary || "#7e22ce"};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessageDiv = styled.div`
  color: #ef4444;
  background-color: #fee2e2;
  padding: 16px;
  border-radius: 8px;
  margin: 20px 0;
  border-left: 4px solid #ef4444;
`;

const SelectionSummary = styled.div`
  background-color: ${({ theme }) => theme.primaryLight || "#c4b5fd"}20;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const SummaryItem = styled.div`
  display: flex;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SummaryLabel = styled.div`
  font-weight: bold;
  margin-right: 8px;
  min-width: 100px;
`;

const SummaryValue = styled.div`
  color: ${({ theme }) => theme.primary || "#7e22ce"};
`;

const LearningFlow: React.FC = () => {
  // Steps: 1 – Term, 2 – Subject, 3 – Strand, 4 – Sub-Strand, 5 – MidLesson, 6 – PostLesson.
  const { user } = useAuth();
  const grade = user?.grade || "7";

  const [step, setStep] = useState<number>(1);
  const [selectedTerm, setSelectedTerm] = useState<number | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [selectedStrand, setSelectedStrand] = useState<string | null>(null);
  const [selectedSubStrand, setSelectedSubStrand] = useState<string | null>(null);

  // Lesson data fetched from backend.
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loadingLesson, setLoadingLesson] = useState<boolean>(false);
  const [lessonError, setLessonError] = useState<string>("");

  // Current module index for mid-lesson.
  const [currentModuleIndex, setCurrentModuleIndex] = useState<number>(0);

  // Debug: log state changes.
  useEffect(() => {
    console.log("Current Step:", step);
    console.log("Selected Term:", selectedTerm);
    console.log("Selected Subject:", selectedSubject);
    console.log("Selected Strand:", selectedStrand);
    console.log("Selected Sub-Strand:", selectedSubStrand);
  }, [step, selectedTerm, selectedSubject, selectedStrand, selectedSubStrand]);

  // Fetch lesson data once all selections are provided.
  useEffect(() => {
    if (
      step === 4 &&
      selectedTerm &&
      selectedSubject &&
      selectedStrand &&
      selectedSubStrand
    ) {
      const fetchLesson = async () => {
        setLoadingLesson(true);
        setLessonError("");
        try {
          const { data } = await api.get(`/lessons`, {
            params: {
              grade,
              term: selectedTerm.toString(),
              subject: selectedSubject,
              strand: selectedStrand,
              subStrand: selectedSubStrand,
            },
          });
          if (data.success) {
            setLesson(data.data.lesson);
          } else {
            setLessonError("Lesson not found based on provided filters");
          }
        } catch (error: any) {
          setLessonError(
            error.response?.data?.message || "Error fetching lesson data"
          );
          console.error("Error fetching lesson data:", error);
        } finally {
          setLoadingLesson(false);
        }
      };
      fetchLesson();
    }
  }, [step, selectedTerm, selectedSubject, selectedStrand, selectedSubStrand, grade]);

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Handlers for selection steps.
  const handleTermSelect = (term: number) => {
    setSelectedTerm(term);
    setSelectedSubject(null);
    setSelectedStrand(null);
    setSelectedSubStrand(null);
    setStep(2);
  };

  const handleSubjectSelect = (subject: string) => {
    setSelectedSubject(subject);
    setSelectedStrand(null);
    setSelectedSubStrand(null);
    setStep(3);
  };

  const handleStrandSelect = (strand: string) => {
    setSelectedStrand(strand);
    setSelectedSubStrand(null);
    setStep(4);
  };

  const handleSubStrandSelect = (subStrand: string) => {
    setSelectedSubStrand(subStrand);
    setStep(5);
  };

  const handleBeginLesson = () => {
    if (selectedTerm && selectedSubject && selectedStrand && selectedSubStrand) {
      console.log("Beginning lesson with selections:", {
        grade,
        term: selectedTerm,
        subject: selectedSubject,
        strand: selectedStrand,
        subStrand: selectedSubStrand,
      });
      setCurrentModuleIndex(0);
      setStep(5);
    } else {
      console.warn("Missing selection(s) – cannot begin lesson");
    }
  };

  // Handle mid-lesson module completion.
  // In LearningFlow.tsx, update your handleModuleComplete:
  const handleModuleComplete = async (moduleId: string, data?: any) => {
    console.log("Module completed:", moduleId, data);
    try {
      // Build the progress payload using a fallback for data:
      const progressPayload = {
        userId: user?.id || "CURRENT_USER_ID",
        progress: {
          contentId: lesson?.id,
          moduleSegmentId: moduleId,
          completionPercentage:
            ((currentModuleIndex + 1) / (lesson?.modules?.length || 1)) * 100,
          data: data || {} // ensure it's always an object
        }
      };
  
      let existingProgress = null;
  
      // First, try to fetch existing progress data for this user.
      try {
        const res = await api.get(
          `/analytics/progressdata/user/${user?.id}`
        );
        if (res.data && res.data.data) {
          existingProgress = res.data.data;
        }
      } catch (getError: any) {
        // If not found, that's okay (we'll create one with POST).
        console.log("No existing progress data found, will create one.");
      }
  
      if (existingProgress && existingProgress._id) {
        // If progress data exists, update it (PUT)
        await api.put(
          `/analytics/progressdata/${existingProgress._id}`,
          progressPayload
        );
      } else {
        // Otherwise, create new progress data (POST)
        await api.post(
          `/analytics/progressdata`,
          progressPayload
        );
      }
  
      // Advance to the next module or proceed to post-lesson.
      if (lesson && lesson.modules && currentModuleIndex < lesson.modules.length - 1) {
        setCurrentModuleIndex(currentModuleIndex + 1);
      } else {
        setStep(6);
      }
    } catch (error: any) {
      setLessonError(error.response?.data?.message || "Error updating progress data");
      console.error("Error updating progress/analytics:", error);
    }
  };
  

  // Calculate progress percentage for the selection progress bar.
  const getProgressBarWidth = () => {
    const totalSteps = 4; // Total selection steps.
    const completedSteps = step > 4 ? 4 : step - 1;
    return `${(completedSteps / totalSteps) * 100}%`;
  };
  
  // Define the steps for the progress indicator.
  const progressSteps = [
    { label: "Term", step: 1 },
    { label: "Subject", step: 2 },
    { label: "Strand", step: 3 },
    { label: "Sub-Strand", step: 4 },
  ];

  // Custom loading state component.
  const renderLoading = () => (
    <LoadingOverlay>
      <LoadingSpinner />
      <div>Loading lesson content...</div>
    </LoadingOverlay>
  );

  // Custom error state component.
  const renderError = () => (
    <ErrorMessageDiv>
      <strong>Oops!</strong> {lessonError}
    </ErrorMessageDiv>
  );

  // Selection summary component.
  const renderSelectionSummary = () => {
    if (step < 2) return null;
    return (
      <SelectionSummary>
        {selectedTerm && (
          <SummaryItem>
            <SummaryLabel>Term:</SummaryLabel>
            <SummaryValue>Term {selectedTerm}</SummaryValue>
          </SummaryItem>
        )}
        {selectedSubject && (
          <SummaryItem>
            <SummaryLabel>Subject:</SummaryLabel>
            <SummaryValue>{selectedSubject}</SummaryValue>
          </SummaryItem>
        )}
        {selectedStrand && (
          <SummaryItem>
            <SummaryLabel>Strand:</SummaryLabel>
            <SummaryValue>{selectedStrand}</SummaryValue>
          </SummaryItem>
        )}
        {selectedSubStrand && (
          <SummaryItem>
            <SummaryLabel>Sub-Strand:</SummaryLabel>
            <SummaryValue>{selectedSubStrand}</SummaryValue>
          </SummaryItem>
        )}
      </SelectionSummary>
    );
  };

  return (
    <Container>
      {/* Navigation and progress indicator for steps 1-4 */}
      {step <= 4 && (
        <>
          <BackButtonContainer>
            {step > 1 && (
              <BackButton onClick={handleBack}>
                <FiArrowLeft style={{ marginRight: "8px" }} /> Back
              </BackButton>
            )}
            <div>Grade {grade}</div>
          </BackButtonContainer>

          <ProgressContainer>
            <ProgressBar />
            <ProgressFill width={getProgressBarWidth()} />
            {progressSteps.map((stepInfo) => (
              <StepWrapper key={stepInfo.step}>
                <StepCircle 
                  active={step === stepInfo.step} 
                  completed={step > stepInfo.step}
                >
                  {step > stepInfo.step ? (
                    <FiCheckCircle size={18} />
                  ) : (
                    stepInfo.step
                  )}
                </StepCircle>
                <StepLabel 
                  active={step === stepInfo.step}
                  completed={step > stepInfo.step}
                >
                  {stepInfo.label}
                </StepLabel>
              </StepWrapper>
            ))}
          </ProgressContainer>

          {renderSelectionSummary()}
        </>
      )}

      {/* Lesson module progress bar for step 5 */}
      {step === 5 && lesson?.modules && lesson.modules.length > 0 && (
        <BackButtonContainer>
          <BackButton onClick={handleBack}>
            <FiArrowLeft style={{ marginRight: "8px" }} /> Back
          </BackButton>
          <ModuleProgress>
            <div>
              Module {currentModuleIndex + 1} of {lesson.modules.length}
            </div>
            <div>
              {Math.round(
                (currentModuleIndex / (lesson.modules.length - 1)) * 100
              )}
              % Complete
            </div>
          </ModuleProgress>
        </BackButtonContainer>
      )}

      {/* Main content area */}
      {loadingLesson ? (
        renderLoading()
      ) : lessonError ? (
        renderError()
      ) : (
        <>
          {step === 1 && <TermSelection onSelect={handleTermSelect} />}

          {step === 2 && selectedTerm !== null && (
            <SubjectList
              term={selectedTerm}
              grade={grade}
              onSubjectSelect={handleSubjectSelect}
            />
          )}

          {step === 3 && selectedTerm && selectedSubject && (
            <StrandList
              subject={selectedSubject}
              term={selectedTerm}
              grade={grade}
              onStrandSelect={handleStrandSelect}
            />
          )}

          {step === 4 && selectedTerm && selectedSubject && selectedStrand && (
            <>
              <SubStrandList
                subject={selectedSubject}
                strand={selectedStrand}
                term={selectedTerm}
                grade={grade}
                onSubStrandSelect={handleSubStrandSelect}
              />
              {selectedSubStrand && (
                <Button onClick={handleBeginLesson} disabled={!selectedSubStrand}>
                  Begin Lesson
                </Button>
              )}
            </>
          )}

          {step === 5 && lesson && lesson.modules && lesson.modules.length > 0 ? (
            <ModuleRenderer
              module={lesson.modules[currentModuleIndex]}
              onComplete={handleModuleComplete}
            />
          ) : (
            step === 5 && (
              <LessonViewer
                grade={grade}
                term={selectedTerm!}
                subject={selectedSubject!}
                strand={selectedStrand!}
                subStrand={selectedSubStrand!}
              />
            )
          )}

          {step === 6 && (
            <PostLesson
              onFinish={() => {
                setStep(1);
                setSelectedTerm(null);
                setSelectedSubject(null);
                setSelectedStrand(null);
                setSelectedSubStrand(null);
              }}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default LearningFlow;
